import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { canUseLiveContext } from "@/lib/plans";
import {
  startConnect,
  completeConnect,
  disconnect,
  connectionSummary,
} from "./google-connect.server";

const connectorSchema = z.object({
  connectorId: z.enum(["google_calendar", "google_mail"]),
});

const startSchema = connectorSchema.extend({
  switchAccount: z.boolean().optional(),
});

export const getGoogleConnections = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => connectionSummary(context.userId));

export const startGoogleConnect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => startSchema.parse(input))
  .handler(async ({ context, data }) => {
    const request = getRequest();
    if (!request) throw new Error("OAuth must start from an app request.");
    // The gate isn't only cosmetic: a free account can't start consent at all.
    const { data: profile } = await context.supabase
      .from("profiles")
      .select("plan")
      .eq("id", context.userId)
      .maybeSingle();
    if (!canUseLiveContext((profile as any)?.plan)) {
      throw new Error("Live Context is part of Pro.");
    }
    if (data.switchAccount) {
      // Drop the existing connection first so consent starts clean on the
      // account the user picks, instead of silently re-authorizing the old one.
      await disconnect(context.userId, data.connectorId);
    }
    return startConnect(context.userId, data.connectorId, request.url, {
      forceAccountPicker: !!data.switchAccount,
    });
  });

export const completeGoogleConnect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ code: z.string().min(1) }).parse(input))
  .handler(async ({ context, data }) => completeConnect(context.userId, data.code));

export const disconnectGoogleConnector = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => connectorSchema.parse(input))
  .handler(async ({ context, data }) => disconnect(context.userId, data.connectorId));
